import { ToastContext } from "context/ToastContext";
import { Surface } from "pages/HospitalsPage/components/BenefitSelection/Surface";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputSwitch } from "primereact/inputswitch";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { useContext, useState } from "react";
import {
  useCreatePayerAutoSelectionRuleMutation,
  useDeletePayerAutoSelectionRuleMutation,
  useGetPayerAutoSelectionRulesQuery,
  useReorderPayerAutoSelectionRulesMutation,
  useUpdatePayerAutoSelectionRuleMutation,
} from "store/queries/payerAutoSelection";
import styled from "styled-components";
import {
  IPayerAutoSelectionRule,
  IPayerAutoSelectionRuleSaveRequest,
} from "types/PayerAutoSelection/PayerAutoSelection";

import { PayerAutoSelectionRuleDialog } from "./PayerAutoSelectionRuleDialog";
import { PayerAutoSelectionTester } from "./PayerAutoSelectionTester";
import {
  INTRO,
  KIND_LABELS,
  MEDICARE_ADVANTAGE_TARGET,
  OUT_OF_AREA_CONDITION,
  accountsInWords,
  optionsInWords,
  payerTypesInWords,
  sourceInWords,
} from "./payerAutoSelectionWording";

const StyledDataTable = styled(DataTable)`
  .p-datatable-thead > tr > th {
    background: #ffffff;
  }
  .p-datatable-tbody > tr > td {
    vertical-align: top;
  }
  .p-datatable-tbody > tr.payer-auto-selection-off > td {
    opacity: 0.55;
  }
`;

const chip = (text: string, className = "surface-100 text-color") => (
  <Tag key={text} value={text} className={`${className} font-normal`} />
);

/** The PUT body that re-saves a rule unchanged apart from `patch`. */
export const ruleToSaveRequest = (
  rule: IPayerAutoSelectionRule,
  patch: Partial<IPayerAutoSelectionRuleSaveRequest> = {}
): IPayerAutoSelectionRuleSaveRequest => ({
  kind: rule.kind,
  keywords: rule.keywords,
  excludeKeywords: rule.excludeKeywords ?? [],
  matchPolicyTypeMedicaid: rule.matchPolicyTypeMedicaid,
  targetPayerId: rule.targetPayerId,
  accountIds: rule.accountIds,
  sourceScope: rule.sourceScope,
  sourcePayerIds: rule.sourcePayerIds,
  sourcePayerTypes: rule.sourcePayerTypes ?? [],
  wholeSegment: rule.wholeSegment,
  skipOutOfState: rule.skipOutOfState,
  enabled: rule.enabled,
  priority: rule.priority,
  ...patch,
});

const targetName = (rule: IPayerAutoSelectionRule): string => {
  if (rule.kind === "MEDICARE_ADVANTAGE") return MEDICARE_ADVANTAGE_TARGET;
  return rule.targetPayer?.name ?? `Payer #${rule.targetPayerId}`;
};

/**
 * CLE-2845 — Eligibility › Payer Auto-Selection. After every eligibility check these rules decide
 * whether the estimate's payer should change to the one the response says the patient really has.
 *
 * ONE ordered list, not stages: the API walks it top to bottom and the first rule that fires wins,
 * so the order IS the precedence (Select Plus sits above Blue Select because "BLUE SELECT PLUS"
 * contains "BLUE SELECT"). Dragging a row saves the whole order at once.
 *
 * Medicare Advantage is an ordinary row (seeded with its words, exclude words and "Commercial
 * only"); it can be edited and removed like any other, and without one nothing switches to a
 * Medicare Advantage payer.
 */
export function PayerAutoSelectionTab() {
  const toast = useContext(ToastContext);
  const {
    data: rules,
    isLoading,
    isError,
  } = useGetPayerAutoSelectionRulesQuery();
  const [createRule, { isLoading: creating }] =
    useCreatePayerAutoSelectionRuleMutation();
  const [updateRule, { isLoading: updating }] =
    useUpdatePayerAutoSelectionRuleMutation();
  const [deleteRule] = useDeletePayerAutoSelectionRuleMutation();
  const [reorderRules] = useReorderPayerAutoSelectionRulesMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [testerOpen, setTesterOpen] = useState(false);
  const [editing, setEditing] = useState<IPayerAutoSelectionRule | null>(null);

  const allRules = rules ?? [];
  const nextPriority =
    allRules.reduce((max, rule) => Math.max(max, rule.priority), -1) + 1;

  const fail = (summary: string, error: any, fallback: string) =>
    toast?.current?.show({
      severity: "error",
      summary,
      detail: error?.data?.message || error?.message || fallback,
    });

  const handleSave = async (body: IPayerAutoSelectionRuleSaveRequest) => {
    try {
      if (editing) {
        await updateRule({ id: editing.id, body }).unwrap();
      } else {
        await createRule(body).unwrap();
      }
      setDialogOpen(false);
      toast?.current?.show({
        severity: "success",
        summary: "Rule saved",
        detail: "The next eligibility check uses it.",
      });
    } catch (error: any) {
      fail("Not saved", error, "The rule could not be saved.");
    }
  };

  const toggle = async (rule: IPayerAutoSelectionRule, enabled: boolean) => {
    try {
      await updateRule({
        id: rule.id,
        body: ruleToSaveRequest(rule, { enabled }),
      }).unwrap();
    } catch (error: any) {
      fail("Not changed", error, "The rule could not be changed.");
    }
  };

  const remove = async (rule: IPayerAutoSelectionRule) => {
    // eslint-disable-next-line no-alert
    const sure = window.confirm(
      `Remove the ${KIND_LABELS[rule.kind]} rule that switches to "${targetName(
        rule
      )}" (${accountsInWords(
        rule
      )})? The next eligibility check stops using it.`
    );
    if (!sure) return;
    try {
      await deleteRule(rule.id).unwrap();
    } catch (error: any) {
      fail("Not removed", error, "The rule could not be removed.");
    }
  };

  const reorder = async (ordered: IPayerAutoSelectionRule[]) => {
    try {
      await reorderRules({ ids: ordered.map((rule) => rule.id) }).unwrap();
    } catch (error: any) {
      fail("Order not saved", error, "The new order could not be saved.");
    }
  };

  const openEditor = (rule: IPayerAutoSelectionRule | null) => {
    setEditing(rule);
    setDialogOpen(true);
  };

  const positionBody = (_rule: IPayerAutoSelectionRule, options: any) => (
    <span className="text-color-secondary">{options.rowIndex + 1}</span>
  );

  const containsBody = (rule: IPayerAutoSelectionRule) => (
    <div
      className="flex flex-wrap gap-1 align-items-center"
      data-testid={`payerAutoSelection.rule.${rule.id}.contains`}
    >
      {rule.kind === "OUT_OF_AREA_DEFAULT" ? (
        <span className="text-color-secondary">{OUT_OF_AREA_CONDITION}</span>
      ) : (
        <>
          {rule.keywords.map((keyword) =>
            chip(keyword, "bg-indigo-100 text-indigo-900 font-mono")
          )}
          {rule.matchPolicyTypeMedicaid && (
            <span className="text-color-secondary">
              or pVerify says Medicaid
            </span>
          )}
        </>
      )}
    </div>
  );

  const excludesBody = (rule: IPayerAutoSelectionRule) => (
    <div
      className="flex flex-wrap gap-1 align-items-center"
      data-testid={`payerAutoSelection.rule.${rule.id}.excludes`}
    >
      {(rule.excludeKeywords ?? []).length > 0 ? (
        rule.excludeKeywords.map((word) =>
          chip(word, "bg-red-50 text-red-800 font-mono")
        )
      ) : (
        <span className="text-color-secondary">—</span>
      )}
    </div>
  );

  const ruleBody = (rule: IPayerAutoSelectionRule) => (
    <span
      className="font-semibold"
      data-testid={`payerAutoSelection.rule.${rule.id}`}
    >
      {KIND_LABELS[rule.kind]}
    </span>
  );

  const targetBody = (rule: IPayerAutoSelectionRule) => (
    <span className={rule.kind === "MEDICARE_ADVANTAGE" ? "" : "font-medium"}>
      {targetName(rule)}
    </span>
  );

  const appliesBody = (rule: IPayerAutoSelectionRule) => (
    <div className="flex flex-column gap-1">
      <span>{accountsInWords(rule)}</span>
      <span className="text-color-secondary">{sourceInWords(rule)}</span>
      {payerTypesInWords(rule) && (
        <span className="text-color-secondary">{payerTypesInWords(rule)}</span>
      )}
      {optionsInWords(rule).length > 0 && (
        <div className="flex flex-wrap gap-1">
          {optionsInWords(rule).map((option) =>
            chip(option, "bg-yellow-50 text-yellow-900")
          )}
        </div>
      )}
    </div>
  );

  const enabledBody = (rule: IPayerAutoSelectionRule) => (
    <InputSwitch
      checked={rule.enabled}
      onChange={(event) => toggle(rule, !!event.value)}
      aria-label="Rule enabled"
      data-testid={`payerAutoSelection.rule.${rule.id}.enabled`}
    />
  );

  const actionsBody = (rule: IPayerAutoSelectionRule) => (
    <div className="flex gap-1 justify-content-end">
      <Button
        type="button"
        icon="pi pi-pencil"
        className="p-button-text p-button-sm"
        aria-label={`Edit ${KIND_LABELS[rule.kind]} rule`}
        data-testid={`payerAutoSelection.rule.${rule.id}.edit`}
        onClick={() => openEditor(rule)}
      />
      <Button
        type="button"
        icon="pi pi-trash"
        className="p-button-text p-button-sm p-button-danger"
        aria-label={`Remove ${KIND_LABELS[rule.kind]} rule`}
        data-testid={`payerAutoSelection.rule.${rule.id}.remove`}
        onClick={() => remove(rule)}
      />
    </div>
  );

  return (
    <div className="flex flex-column gap-3" data-testid="payerAutoSelectionTab">
      <Surface>
        <div className="flex flex-wrap gap-2 mb-2">
          <Button
            type="button"
            icon="pi pi-plus"
            label="Add rule"
            className="p-button-sm"
            data-testid="payerAutoSelection.add"
            onClick={() => openEditor(null)}
          />
          <Button
            type="button"
            icon="pi pi-play"
            label="Test a response"
            className="p-button-sm p-button-outlined"
            data-testid="payerAutoSelection.test"
            onClick={() => setTesterOpen(true)}
          />
        </div>
        <p className="mt-0 mb-3 text-color-secondary">{INTRO}</p>

        {isLoading && (
          <div className="flex justify-content-center p-3">
            <ProgressSpinner
              style={{ width: "2rem", height: "2rem" }}
              data-testid="payerAutoSelection.loading"
            />
          </div>
        )}
        {isError && (
          <Message
            severity="error"
            text="The Payer Auto-Selection rules could not be loaded."
            data-testid="payerAutoSelection.error"
          />
        )}
        {!isLoading && !isError && allRules.length === 0 && (
          <span
            className="text-color-secondary"
            data-testid="payerAutoSelection.empty"
          >
            No rules yet, so no eligibility check changes the payer.
          </span>
        )}
        {!isLoading && !isError && allRules.length > 0 && (
          <StyledDataTable
            value={allRules}
            dataKey="id"
            reorderableRows
            onRowReorder={(event: any) => reorder(event.value)}
            rowClassName={(rule: IPayerAutoSelectionRule) => ({
              "payer-auto-selection-off": !rule.enabled,
            })}
            className="w-full"
            data-testid="payerAutoSelection.table"
          >
            <Column rowReorder style={{ width: "2.5rem" }} />
            <Column header="#" body={positionBody} style={{ width: "3rem" }} />
            <Column header="Rule" body={ruleBody} style={{ width: "11rem" }} />
            <Column
              header="Plan name contains"
              body={containsBody}
              style={{ width: "22rem", maxWidth: "22rem" }}
            />
            <Column
              header="Plan name does not contain"
              body={excludesBody}
              style={{ width: "22rem", maxWidth: "22rem" }}
            />
            <Column
              header="Switch to"
              body={targetBody}
              style={{ width: "14rem" }}
            />
            <Column
              header="Applies to"
              body={appliesBody}
              style={{ width: "16rem" }}
            />
            <Column
              header="Enabled"
              body={enabledBody}
              style={{ width: "6rem" }}
            />
            <Column body={actionsBody} style={{ width: "6rem" }} />
          </StyledDataTable>
        )}
      </Surface>

      <PayerAutoSelectionTester
        visible={testerOpen}
        onHide={() => setTesterOpen(false)}
      />

      <PayerAutoSelectionRuleDialog
        visible={dialogOpen}
        rule={editing}
        saving={creating || updating}
        nextPriority={nextPriority}
        onSave={handleSave}
        onHide={() => setDialogOpen(false)}
      />
    </div>
  );
}
